// src/oura/primitives/SourceChip.tsx
//
// Where a generated fact came from (ourteam-frontend#3756, backend#4836, K-1).
//
// A chip over a sentence is how a reviewer knows a price came from the client's
// own price list rather than from a model's imagination. Without it the feature
// is unreviewable: the copy simply looks more confident.
//
// ONE DEFINITION. No screen may declare its own — a second chip is a second
// answer to "is this sourced", and the two will disagree.
//
// The backend verifies every quote against its source before sending it, so
// nothing here re-checks one. It must also never INVENT a chip: an empty
// `citations` array means the sentence has no source, and rendering nothing is
// the honest thing to show. Most businesses have no documents at all, so empty
// is the normal case and not an empty state.

import { useState } from "react";
import { FileTextIcon, FileXIcon, StickyNoteIcon, TriangleAlertIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { shortDate } from "../lib/oura";
import type { Citation } from "@service/aeo/types";

/** Over this old, a document may no longer describe the business it describes. */
const STALE_AFTER_DAYS = 365;

export function isStaleCitation(c: Citation, now = new Date()): boolean {
    if (!c.content_as_of) return false;
    const d = new Date(`${c.content_as_of}T00:00:00`);
    if (Number.isNaN(d.getTime())) return false;
    return (now.getTime() - d.getTime()) / 86_400_000 > STALE_AFTER_DAYS;
}

/** A staff note rather than a merchant's own file. */
const isNote = (c: Citation) => (c.label ?? "").toLowerCase() === "note";

/**
 * The source was deleted after the draft was written. Shown, never hidden: a
 * reviewer should see that the evidence is gone, not that it never existed.
 * Explicit `=== true`, so a payload without the flag reads as live.
 */
const isDeletedCitation = (c: Citation) => c.deleted === true;

/**
 * "Price list 2026.pdf" -> "Price list 2026". Extension stripped, trimmed, so a
 * chip stays a chip and not a filename.
 */
function shortTitle(title: string, max = 18): string {
    const base = title.replace(/\.[a-z0-9]{1,5}$/i, "").trim() || title;
    return base.length > max ? `${base.slice(0, max - 1).trimEnd()}…` : base;
}

/**
 * The chips for one claim.
 *
 * `citations` empty renders nothing at all — see the header. More than one
 * source backing the same claim collapses to the first plus `+N`, because a row
 * of filenames is not information a reviewer can use at a glance.
 */
export function SourceChip({ citations, className }: { citations: Citation[]; className?: string }) {
    const [open, setOpen] = useState<Citation | null>(null);
    if (!citations || citations.length === 0) return null;

    const [first, ...rest] = citations;
    return (
        <>
            <span className={cn("inline-flex flex-wrap items-center gap-1", className)}>
                <ChipButton citation={first} onClick={() => setOpen(first)} />
                {rest.length > 0 && (
                    <button
                        type="button"
                        onClick={() => setOpen(rest[0])}
                        className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
                    >
                        +{rest.length}
                    </button>
                )}
            </span>
            {open && <QuoteDialog citation={open} onClose={() => setOpen(null)} />}
        </>
    );
}

function ChipButton({ citation, onClick }: { citation: Citation; onClick: () => void }) {
    // Deleted wins over every other state: whether a gone document was a note
    // or out of date no longer matters, only that it is gone.
    const deleted = isDeletedCitation(citation);
    const stale = !deleted && isStaleCitation(citation);
    const note = !deleted && isNote(citation);
    const Icon = deleted ? FileXIcon : note ? StickyNoteIcon : stale ? TriangleAlertIcon : FileTextIcon;
    return (
        <button
            type="button"
            onClick={onClick}
            title={deleted ? `${citation.source_title} (deleted)` : citation.source_title}
            className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
                // Dead grey, struck through: the evidence existed and is gone.
                deleted && "border-gray-200 bg-gray-50 text-gray-400 line-through hover:bg-gray-100",
                // A staff note is OURS, not the merchant's — dashed, so it never
                // reads as something the client handed us.
                note && "border-dashed border-gray-300 bg-white text-gray-600 hover:bg-gray-50",
                // Amber, not red: an old price list may still be perfectly right.
                stale && !note && "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100",
                !deleted && !note && !stale && "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100",
            )}
        >
            <Icon className="h-3 w-3" />
            {shortTitle(citation.source_title)}
        </button>
    );
}

/** The quoted passage, with what it is true for. */
function QuoteDialog({ citation, onClose }: { citation: Citation; onClose: () => void }) {
    const deleted = isDeletedCitation(citation);
    const stale = !deleted && isStaleCitation(citation);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="relative z-10 w-full max-w-lg rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
                <p className="text-sm font-semibold text-gray-900">{citation.source_title}</p>
                <p className="mt-0.5 text-xs text-gray-500">
                    {citation.label ? <span className="capitalize">{citation.label}</span> : "Document"}
                    {citation.content_as_of
                        ? <> · true as of {shortDate(citation.content_as_of)}</>
                        : <> · no date on the content</>}
                </p>
                {deleted && (
                    <p className="mt-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-gray-600">
                        This document has since been deleted. The passage below is what
                        the draft was written from; nothing backs it any more.
                    </p>
                )}
                {stale && (
                    <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                        This is over a year old. It may still be right — check before acting on it.
                    </p>
                )}
                <blockquote className="mt-3 border-l-2 border-gray-200 pl-3 text-sm leading-relaxed text-gray-700">
                    {citation.quote}
                </blockquote>
                <p className="mt-3 text-xs text-gray-400">
                    {/* Said plainly, because it changes what the number may be used
                        for: a merchant's document feeds writing and never moves a
                        measured score. */}
                    The merchant told us this. It is not something we measured.
                </p>
                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
